import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { demonstrationsApi, dancersApi, movesApi, patternsApi, getAdminMediaUrl } from '../services/api';
import { Demonstration, DemonstrationType } from '../types';

const DemonstrationsManagement: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'MOVE' | 'PATTERN'>('all');
  const [dancerFilter, setDancerFilter] = useState('');
  const [moveFilter, setMoveFilter] = useState('');
  const [patternFilter, setPatternFilter] = useState('');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadDemoType, setUploadDemoType] = useState<DemonstrationType>('MOVE');
  const [uploadDancerId, setUploadDancerId] = useState('');
  const [uploadMoveId, setUploadMoveId] = useState('');
  const [uploadPatternId, setUploadPatternId] = useState('');
  const [uploadCaption, setUploadCaption] = useState('');
  const [autoApprove, setAutoApprove] = useState(true);
  const [uploadError, setUploadError] = useState('');
  const [editingDemo, setEditingDemo] = useState<Demonstration | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [editDancerId, setEditDancerId] = useState('');
  const [editMoveId, setEditMoveId] = useState('');
  const [editPatternId, setEditPatternId] = useState('');
  const [editError, setEditError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const { data: demonstrationsData, isLoading } = useQuery({
    queryKey: ['demonstrations', { statusFilter, typeFilter, dancerFilter, moveFilter, patternFilter }],
    queryFn: () => demonstrationsApi.getAll({
      dancerId: dancerFilter ? Number(dancerFilter) : undefined,
      moveId: moveFilter ? Number(moveFilter) : undefined,
      patternId: patternFilter ? Number(patternFilter) : undefined,
      demonstrationType: typeFilter === 'all' ? undefined : typeFilter,
      approved: statusFilter === 'approved' ? true : statusFilter === 'pending' ? false : statusFilter === 'all' ? 'all' as const : undefined,
      featured: statusFilter === 'featured' ? true : undefined,
    }),
  });

  const { data: dancersData } = useQuery({
    queryKey: ['dancers-list'],
    queryFn: () => dancersApi.getAll({ limit: 100 }),
  });

  const { data: movesData } = useQuery({
    queryKey: ['moves-list'],
    queryFn: () => movesApi.getAll({ limit: 100 }),
  });

  const { data: patternsData } = useQuery({
    queryKey: ['patterns-list'],
    queryFn: () => patternsApi.getAll({ limit: 100 }),
  });

  const { data: featuredDemo } = useQuery({
    queryKey: ['featured-demonstration'],
    queryFn: () => demonstrationsApi.getFeatured(),
  });

  const approveMutation = useMutation({
    mutationFn: demonstrationsApi.approve,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demonstrations'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: demonstrationsApi.reject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demonstrations'] });
    },
  });

  const featureMutation = useMutation({
    mutationFn: demonstrationsApi.feature,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demonstrations'] });
      queryClient.invalidateQueries({ queryKey: ['featured-demonstration'] });
    },
  });

  const unfeatureMutation = useMutation({
    mutationFn: demonstrationsApi.unfeature,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demonstrations'] });
      queryClient.invalidateQueries({ queryKey: ['featured-demonstration'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: demonstrationsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demonstrations'] });
      queryClient.invalidateQueries({ queryKey: ['featured-demonstration'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { caption?: string; dancerId?: number; moveId?: number; patternId?: number } }) =>
      demonstrationsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demonstrations'] });
      queryClient.invalidateQueries({ queryKey: ['featured-demonstration'] });
      setEditingDemo(null);
    },
    onError: (error: any) => {
      setEditError(error.response?.data?.error || 'Failed to update demonstration');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: demonstrationsApi.adminUpload,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['demonstrations'] });
      resetUploadForm();
    },
    onError: (error: any) => {
      setUploadError(error.response?.data?.error || 'Failed to upload demonstration');
    },
  });

  const resetUploadForm = () => {
    setShowUploadForm(false);
    setUploadDemoType('MOVE');
    setUploadDancerId('');
    setUploadMoveId('');
    setUploadPatternId('');
    setUploadCaption('');
    setAutoApprove(true);
    setUploadError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError('');

    if (!uploadDancerId) {
      setUploadError('Please select a dancer');
      return;
    }
    if (uploadDemoType === 'MOVE' && !uploadMoveId) {
      setUploadError('Please select a move');
      return;
    }
    if (uploadDemoType === 'PATTERN' && !uploadPatternId) {
      setUploadError('Please select a pattern');
      return;
    }

    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setUploadError('Please select a video file');
      return;
    }

    const formData = new FormData();
    formData.append('video', file);
    formData.append('dancerId', uploadDancerId);
    if (uploadDemoType === 'MOVE') {
      formData.append('moveId', uploadMoveId);
    } else {
      formData.append('patternId', uploadPatternId);
    }
    formData.append('caption', uploadCaption);
    formData.append('autoApprove', String(autoApprove));

    uploadMutation.mutate(formData);
  };

  const handleApprove = (demo: Demonstration) => {
    approveMutation.mutate(demo.id);
  };

  const handleReject = (demo: Demonstration) => {
    if (window.confirm('Are you sure you want to reject this demonstration?')) {
      rejectMutation.mutate(demo.id);
    }
  };

  const handleFeature = (demo: Demonstration) => {
    if (featuredDemo && featuredDemo.id !== demo.id) {
      if (!window.confirm('This will replace the current featured demonstration (Move of the Week). Continue?')) {
        return;
      }
    }
    featureMutation.mutate(demo.id);
  };

  const handleUnfeature = (demo: Demonstration) => {
    unfeatureMutation.mutate(demo.id);
  };

  const handleDelete = (demo: Demonstration) => {
    if (window.confirm('Are you sure you want to delete this demonstration? This cannot be undone.')) {
      deleteMutation.mutate(demo.id);
    }
  };

  const handleEdit = (demo: Demonstration) => {
    setEditingDemo(demo);
    setEditCaption(demo.caption || '');
    setEditDancerId(String(demo.dancerId));
    setEditMoveId(demo.moveId != null ? String(demo.moveId) : '');
    setEditPatternId(demo.patternId != null ? String(demo.patternId) : '');
    setEditError('');
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDemo) return;

    const data: { caption: string; dancerId: number; moveId?: number; patternId?: number } = {
      caption: editCaption,
      dancerId: Number(editDancerId),
    };
    if (editingDemo.demonstrationType === 'MOVE') {
      data.moveId = Number(editMoveId);
    } else {
      data.patternId = Number(editPatternId);
    }

    updateMutation.mutate({ id: editingDemo.id, data });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const pendingCount = demonstrationsData?.data.filter(d => !d.isApproved).length || 0;

  return (
    <div className="demonstrations-management">
      <div className="page-header">
        <h1>Demonstrations Management</h1>
        <div className="page-header-actions">
          {pendingCount > 0 && (
            <span className="pending-badge">{pendingCount} pending approval</span>
          )}
          <button
            className="btn btn-primary"
            onClick={() => setShowUploadForm(true)}
            disabled={showUploadForm}
          >
            Upload Demonstration
          </button>
        </div>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="form-card">
          <h2>Upload Demonstration</h2>
          <p className="form-subtitle">Upload a demonstration video on behalf of a dancer</p>
          <form onSubmit={handleUploadSubmit}>
            <div className="form-group">
              <label>Demo Type *</label>
              <div className="radio-group demo-type-options">
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="uploadDemoType"
                    value="MOVE"
                    checked={uploadDemoType === 'MOVE'}
                    onChange={() => setUploadDemoType('MOVE')}
                  />
                  Move
                </label>
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="uploadDemoType"
                    value="PATTERN"
                    checked={uploadDemoType === 'PATTERN'}
                    onChange={() => setUploadDemoType('PATTERN')}
                  />
                  Pattern
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="uploadDancer">Dancer *</label>
                <select
                  id="uploadDancer"
                  value={uploadDancerId}
                  onChange={(e) => setUploadDancerId(e.target.value)}
                  className={!uploadDancerId && uploadError ? 'error' : ''}
                >
                  <option value="">Select a dancer...</option>
                  {dancersData?.data.map(dancer => (
                    <option key={dancer.id} value={dancer.id}>{dancer.name}</option>
                  ))}
                </select>
              </div>

              {uploadDemoType === 'MOVE' ? (
                <div className="form-group">
                  <label htmlFor="uploadMove">Move *</label>
                  <select
                    id="uploadMove"
                    value={uploadMoveId}
                    onChange={(e) => setUploadMoveId(e.target.value)}
                    className={!uploadMoveId && uploadError ? 'error' : ''}
                  >
                    <option value="">Select a move...</option>
                    {movesData?.data.map(move => (
                      <option key={move.id} value={move.id}>
                        {move.name} ({move.style?.name})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="uploadPattern">Pattern *</label>
                  <select
                    id="uploadPattern"
                    value={uploadPatternId}
                    onChange={(e) => setUploadPatternId(e.target.value)}
                    className={!uploadPatternId && uploadError ? 'error' : ''}
                  >
                    <option value="">Select a pattern...</option>
                    {patternsData?.data.map(pattern => (
                      <option key={pattern.id} value={pattern.id}>
                        {pattern.title}{pattern.style?.name ? ` (${pattern.style.name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="uploadVideo">Video File *</label>
              <input
                type="file"
                id="uploadVideo"
                ref={fileInputRef}
                accept="video/*"
              />
              <small className="form-help">Accepted formats: MP4, MOV, AVI. Max size: 100MB</small>
            </div>

            <div className="form-group">
              <label htmlFor="uploadCaption">Caption (optional)</label>
              <textarea
                id="uploadCaption"
                value={uploadCaption}
                onChange={(e) => setUploadCaption(e.target.value)}
                rows={2}
                placeholder="Add a description or notes about this demonstration..."
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={autoApprove}
                  onChange={(e) => setAutoApprove(e.target.checked)}
                />
                Auto-approve this demonstration
              </label>
              <small className="form-help">Instructor uploads are typically pre-approved</small>
            </div>

            {uploadError && <div className="error-message">{uploadError}</div>}

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetUploadForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={uploadMutation.isPending}>
                {uploadMutation.isPending ? 'Uploading...' : 'Upload Demonstration'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Modal */}
      {editingDemo && (
        <div className="form-card">
          <h2>Edit Demonstration</h2>
          <p className="form-subtitle">Update details for this demonstration</p>
          <form onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label>Demo Type</label>
              <input
                type="text"
                value={editingDemo.demonstrationType === 'MOVE' ? 'Move' : 'Pattern'}
                disabled
                className="disabled"
              />
              <small className="form-help">Demo type cannot be changed after creation.</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="editDancer">Dancer *</label>
                <select
                  id="editDancer"
                  value={editDancerId}
                  onChange={(e) => setEditDancerId(e.target.value)}
                >
                  {dancersData?.data.map(dancer => (
                    <option key={dancer.id} value={dancer.id}>{dancer.name}</option>
                  ))}
                </select>
              </div>

              {editingDemo.demonstrationType === 'MOVE' ? (
                <div className="form-group">
                  <label htmlFor="editMove">Move *</label>
                  <select
                    id="editMove"
                    value={editMoveId}
                    onChange={(e) => setEditMoveId(e.target.value)}
                  >
                    {movesData?.data.map(move => (
                      <option key={move.id} value={move.id}>
                        {move.name} ({move.style?.name})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="editPattern">Pattern *</label>
                  <select
                    id="editPattern"
                    value={editPatternId}
                    onChange={(e) => setEditPatternId(e.target.value)}
                  >
                    {patternsData?.data.map(pattern => (
                      <option key={pattern.id} value={pattern.id}>
                        {pattern.title}{pattern.style?.name ? ` (${pattern.style.name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="editCaption">Caption</label>
              <textarea
                id="editCaption"
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                rows={2}
                placeholder="Add a description or notes about this demonstration..."
              />
            </div>

            {editError && <div className="error-message">{editError}</div>}

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setEditingDemo(null)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Featured Demonstration Card */}
      {featuredDemo && (
        <div className="featured-card">
          <div className="featured-header">
            <h3>{featuredDemo.demonstrationType === 'PATTERN' ? 'Pattern of the Week' : 'Move of the Week'}</h3>
            <button
              className="btn btn-small btn-secondary"
              onClick={() => handleUnfeature(featuredDemo)}
            >
              Remove Feature
            </button>
          </div>
          <div className="featured-content">
            <div className="featured-video">
              <video
                src={getAdminMediaUrl(featuredDemo.videoUrl)}
                poster={featuredDemo.thumbnail ? getAdminMediaUrl(featuredDemo.thumbnail) : undefined}
                controls
                muted
              />
            </div>
            <div className="featured-info">
              <h4>{featuredDemo.demonstrationType === 'PATTERN'
                ? (featuredDemo.pattern?.title || 'Unknown Pattern')
                : (featuredDemo.move?.name || featuredDemo.move?.title || 'Unknown Move')}</h4>
              <p>Demonstrated by <strong>{featuredDemo.dancer?.name}</strong></p>
              {featuredDemo.caption && <p className="featured-caption">"{featuredDemo.caption}"</p>}
            </div>
          </div>
        </div>
      )}

      <div className="filters-bar">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
          aria-label="Filter by status"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="featured">Featured</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as 'all' | 'MOVE' | 'PATTERN')}
          className="filter-select"
          aria-label="Filter by demo type"
        >
          <option value="all">All Types</option>
          <option value="MOVE">Move Demos</option>
          <option value="PATTERN">Pattern Demos</option>
        </select>

        <select
          value={dancerFilter}
          onChange={(e) => setDancerFilter(e.target.value)}
          className="filter-select"
          aria-label="Filter by dancer"
        >
          <option value="">All Dancers</option>
          {dancersData?.data.map(dancer => (
            <option key={dancer.id} value={dancer.id}>{dancer.name}</option>
          ))}
        </select>

        {(typeFilter === 'all' || typeFilter === 'MOVE') && (
          <select
            value={moveFilter}
            onChange={(e) => setMoveFilter(e.target.value)}
            className="filter-select"
            aria-label="Filter by move"
          >
            <option value="">All Moves</option>
            {movesData?.data.map(move => (
              <option key={move.id} value={move.id}>{move.name}</option>
            ))}
          </select>
        )}

        {(typeFilter === 'all' || typeFilter === 'PATTERN') && (
          <select
            value={patternFilter}
            onChange={(e) => setPatternFilter(e.target.value)}
            className="filter-select"
            aria-label="Filter by pattern"
          >
            <option value="">All Patterns</option>
            {patternsData?.data.map(pattern => (
              <option key={pattern.id} value={pattern.id}>{pattern.title}</option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <div className="loading">Loading demonstrations...</div>
      ) : (
        <div className="demonstrations-grid">
          {demonstrationsData?.data.map((demo) => (
            <div key={demo.id} className={`demo-card ${!demo.isApproved ? 'pending' : ''} ${demo.isFeatured ? 'featured' : ''}`}>
              <div className="demo-video">
                <video
                  src={getAdminMediaUrl(demo.videoUrl)}
                  poster={demo.thumbnail ? getAdminMediaUrl(demo.thumbnail) : undefined}
                  controls
                  muted
                />
                <div className="demo-badges">
                  {demo.isFeatured && <span className="badge featured">Featured</span>}
                  {!demo.isApproved && <span className="badge pending">Pending</span>}
                </div>
              </div>

              <div className="demo-info">
                <div className="demo-header">
                  <div className="demo-dancer">
                    {demo.dancer?.profileImage ? (
                      <img src={demo.dancer.profileImage} alt={demo.dancer.name} className="dancer-avatar-small" />
                    ) : (
                      <div className="dancer-avatar-small placeholder">
                        {demo.dancer?.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <span>{demo.dancer?.name || 'Unknown'}</span>
                  </div>
                  <span className="demo-date">{formatDate(demo.createdAt)}</span>
                </div>

                <h4 className="demo-move">
                  <span className={`badge demo-type-badge ${demo.demonstrationType === 'PATTERN' ? 'pattern' : 'move'}`}>
                    {demo.demonstrationType === 'PATTERN' ? 'Pattern' : 'Move'}
                  </span>
                  {demo.demonstrationType === 'PATTERN'
                    ? (demo.pattern?.title || 'Unknown Pattern')
                    : (demo.move?.name || demo.move?.title || 'Unknown Move')}
                </h4>
                {demo.caption && <p className="demo-caption">{demo.caption}</p>}

                <div className="demo-actions">
                  {!demo.isApproved ? (
                    <>
                      <button
                        className="btn btn-small btn-success"
                        onClick={() => handleApprove(demo)}
                        disabled={approveMutation.isPending}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-small btn-warning"
                        onClick={() => handleReject(demo)}
                        disabled={rejectMutation.isPending}
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <>
                      {!demo.isFeatured ? (
                        <button
                          className="btn btn-small btn-primary"
                          onClick={() => handleFeature(demo)}
                          disabled={featureMutation.isPending}
                        >
                          Set as Featured
                        </button>
                      ) : (
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => handleUnfeature(demo)}
                          disabled={unfeatureMutation.isPending}
                        >
                          Unfeature
                        </button>
                      )}
                    </>
                  )}
                  <button
                    className="btn btn-small btn-secondary"
                    onClick={() => handleEdit(demo)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleDelete(demo)}
                    disabled={deleteMutation.isPending}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}

          {demonstrationsData?.data.length === 0 && (
            <div className="empty-state-card">
              <p>No demonstrations found.</p>
              <small>Demonstrations will appear here when dancers upload them.</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DemonstrationsManagement;
